import React, { useState, useEffect } from 'react';
import { Modal, Button, Tabs, Tab, Form, Alert, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faCheck } from '@fortawesome/free-solid-svg-icons';

const JobDetailModal = ({ show, job, onClose, onResolve, loading }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [fieldResolutions, setFieldResolutions] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (job) {
      // Initialize field resolutions for failed rules
      const initialResolutions = {};
      job.failedRules?.forEach((rule) => {
        const fieldName = extractFieldNameFromRule(rule);
        if (fieldName) {
          initialResolutions[fieldName] = {
            fieldName,
            resolutionType: 'SKIP',
            value: null,
            createAlias: false,
            extractedValue: job.originalExtractedData?.[fieldName] || '',
            referenceDataType: extractReferenceDataType(rule),
            matchedRecordKey: null,
          };
        }
      });
      setFieldResolutions(initialResolutions);
    }
  }, [job]);

  const extractFieldNameFromRule = (rule) => {
    // Try to extract field name from rule name or error message
    // This is a heuristic - adjust based on actual rule naming conventions
    if (rule.ruleName) {
      const match = rule.ruleName.match(/lookup[_-](\w+)/i);
      if (match) return match[1];
    }
    return `field_${rule.ruleId}`;
  };

  const extractReferenceDataType = (rule) => {
    // Try to extract reference data type from rule name
    if (rule.ruleName) {
      const match = rule.ruleName.match(/(vendor|gl_code|customer|product)/i);
      if (match) return match[1].toUpperCase();
    }
    return 'UNKNOWN';
  };

  const handleResolutionChange = (fieldName, updates) => {
    setFieldResolutions((prev) => ({
      ...prev,
      [fieldName]: {
        ...prev[fieldName],
        ...updates,
      },
    }));
  };

  const handleSubmitResolution = async () => {
    try {
      setSubmitting(true);
      
      // Filter out skipped fields with no value and convert to array
      const resolutions = Object.values(fieldResolutions).filter(
        (res) => res.resolutionType !== 'SKIP' || res.value !== null
      );

      await onResolve({
        fieldResolutions: resolutions,
      });
      
      onClose();
    } catch (err) {
      console.error('Error submitting resolution:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const renderDataComparison = () => {
    if (!job) return null;

    const extractedData = job.originalExtractedData || {};
    const augmentedData = job.partialAugmentedData || {};
    const allKeys = new Set([
      ...Object.keys(extractedData),
      ...Object.keys(augmentedData),
    ]);

    return (
      <div className="row">
        <div className="col-md-6">
          <h5>Original Extracted Data</h5>
          <div className="border rounded p-3 bg-light" style={{ maxHeight: '400px', overflowY: 'auto' }}>
            {Object.entries(extractedData).map(([key, value]) => (
              <div key={key} className="mb-2">
                <strong>{key}:</strong>{' '}
                <code>{JSON.stringify(value)}</code>
              </div>
            ))}
          </div>
        </div>
        <div className="col-md-6">
          <h5>Partial Augmented Data</h5>
          <div className="border rounded p-3 bg-light" style={{ maxHeight: '400px', overflowY: 'auto' }}>
            {Object.entries(augmentedData).map(([key, value]) => (
              <div key={key} className="mb-2">
                <strong>{key}:</strong>{' '}
                <code>{JSON.stringify(value)}</code>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderFailedRules = () => {
    if (!job || !job.failedRules || job.failedRules.length === 0) {
      return <Alert variant="info">No failed rules to display.</Alert>;
    }

    return (
      <div>
        {job.failedRules.map((rule, index) => (
          <div key={index} className="card mb-3">
            <div className="card-header">
              <h6 className="mb-0">
                {rule.ruleName} <span className="badge bg-secondary">{rule.ruleType}</span>
              </h6>
            </div>
            <div className="card-body">
              <div className="mb-2">
                <strong>Execution Order:</strong> {rule.executionOrder}
              </div>
              {rule.reviewReason && (
                <div className="mb-2">
                  <strong>Review Reason:</strong>{' '}
                  <span className="badge bg-warning">{rule.reviewReason}</span>
                </div>
              )}
              {rule.errorMessage && (
                <Alert variant="danger" className="mb-2">
                  <strong>Error:</strong> {rule.errorMessage}
                </Alert>
              )}
              {rule.potentialMatches && rule.potentialMatches.length > 0 && (
                <div className="mt-3">
                  <strong>Potential Matches:</strong>
                  <div className="mt-2">
                    {rule.potentialMatches.map((match, matchIndex) => (
                      <div key={matchIndex} className="border rounded p-2 mb-2">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <span className="badge bg-info">
                            Confidence: {(match.confidence * 100).toFixed(1)}%
                          </span>
                        </div>
                        <div className="small">
                          {Object.entries(match.matchedRecord).map(([key, value]) => (
                            <div key={key}>
                              <strong>{key}:</strong> {JSON.stringify(value)}
                            </div>
                          ))}
                        </div>
                        {match.fieldScores && (
                          <div className="mt-2 small text-muted">
                            <strong>Field Scores:</strong>
                            {Object.entries(match.fieldScores).map(([field, score]) => (
                              <span key={field} className="ms-2">
                                {field}: {(score * 100).toFixed(0)}%
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderResolutionForm = () => {
    if (!job || !job.failedRules || job.failedRules.length === 0) {
      return <Alert variant="info">No fields require resolution.</Alert>;
    }

    return (
      <div>
        {job.failedRules.map((rule, index) => {
          const fieldName = extractFieldNameFromRule(rule);
          const resolution = fieldResolutions[fieldName] || {};
          const extractedValue = job.originalExtractedData?.[fieldName];

          return (
            <div key={index} className="card mb-3">
              <div className="card-header">
                <h6 className="mb-0">{rule.ruleName}</h6>
              </div>
              <div className="card-body">
                <div className="mb-3">
                  <strong>Extracted Value:</strong>{' '}
                  <code>{JSON.stringify(extractedValue)}</code>
                </div>

                <Form.Group className="mb-3">
                  <Form.Label>Resolution Type</Form.Label>
                  <Form.Select
                    value={resolution.resolutionType || 'SKIP'}
                    onChange={(e) =>
                      handleResolutionChange(fieldName, {
                        resolutionType: e.target.value,
                        value: null,
                      })
                    }
                  >
                    <option value="SKIP">Skip Field</option>
                    <option value="MANUAL_VALUE">Enter Manual Value</option>
                    {rule.potentialMatches && rule.potentialMatches.length > 0 && (
                      <option value="SELECTED_MATCH">Select from Matches</option>
                    )}
                  </Form.Select>
                </Form.Group>

                {resolution.resolutionType === 'MANUAL_VALUE' && (
                  <Form.Group className="mb-3">
                    <Form.Label>Manual Value</Form.Label>
                    <Form.Control
                      type="text"
                      value={resolution.value || ''}
                      onChange={(e) =>
                        handleResolutionChange(fieldName, { value: e.target.value })
                      }
                      placeholder="Enter the correct value"
                    />
                  </Form.Group>
                )}

                {resolution.resolutionType === 'SELECTED_MATCH' &&
                  rule.potentialMatches &&
                  rule.potentialMatches.length > 0 && (
                    <Form.Group className="mb-3">
                      <Form.Label>Select Match</Form.Label>
                      <Form.Select
                        value={resolution.value || ''}
                        onChange={(e) => {
                          const selectedMatch = rule.potentialMatches.find(
                            (m, i) => i === parseInt(e.target.value)
                          );
                          if (selectedMatch) {
                            handleResolutionChange(fieldName, {
                              value: selectedMatch.matchedRecord,
                              matchedRecordKey: JSON.stringify(selectedMatch.matchedRecord),
                            });
                          }
                        }}
                      >
                        <option value="">Select a match...</option>
                        {rule.potentialMatches.map((match, matchIndex) => (
                          <option key={matchIndex} value={matchIndex}>
                            {Object.entries(match.matchedRecord)
                              .slice(0, 3)
                              .map(([k, v]) => `${k}: ${v}`)
                              .join(', ')}{' '}
                            ({(match.confidence * 100).toFixed(1)}%)
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  )}

                {(resolution.resolutionType === 'MANUAL_VALUE' ||
                  resolution.resolutionType === 'SELECTED_MATCH') && (
                  <Form.Group className="mb-3">
                    <Form.Check
                      type="checkbox"
                      label="Create alias for future lookups"
                      checked={resolution.createAlias || false}
                      onChange={(e) =>
                        handleResolutionChange(fieldName, {
                          createAlias: e.target.checked,
                        })
                      }
                    />
                    <Form.Text className="text-muted">
                      This will automatically resolve similar values in the future
                    </Form.Text>
                  </Form.Group>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  if (!job) return null;

  return (
    <Modal show={show} onHide={onClose} size="xl" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>
          {job.status === 'FAILED' ? 'Failed Augmentation Job Details' : 'Review Augmentation Job'}
          <div className="text-muted small mt-1">
            Job ID: <code>{job.id}</code> • Status: <Badge bg={job.status === 'FAILED' ? 'danger' : 'warning'}>{job.status}</Badge>
          </div>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="mb-3">
          <div className="row">
            <div className="col-md-6">
              <strong>Customer:</strong> {job.customerName}
            </div>
            <div className="col-md-6">
              <strong>Status Reason:</strong>{' '}
              <span className="badge bg-warning">{job.statusReason}</span>
            </div>
          </div>
        </div>

        <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-3">
          <Tab eventKey="overview" title="Overview">
            {renderDataComparison()}
          </Tab>
          <Tab eventKey="failed-rules" title={`Failed Rules (${job.failedRules?.length || 0})`}>
            {renderFailedRules()}
          </Tab>
          <Tab eventKey="resolution" title="Resolution">
            {renderResolutionForm()}
          </Tab>
        </Tabs>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} disabled={submitting}>
          <FontAwesomeIcon icon={faTimes} /> Close
        </Button>
        {job.status === 'NEEDS_REVIEW_AUGMENTATION' && (
          <Button
            variant="primary"
            onClick={handleSubmitResolution}
            disabled={submitting || loading}
          >
            <FontAwesomeIcon icon={faCheck} />{' '}
            {submitting ? 'Submitting...' : 'Submit Resolution'}
          </Button>
        )}
        {job.status === 'FAILED' && (
          <Alert variant="info" className="mb-0 me-3">
            <i className="bi bi-info-circle me-2"></i>
            This job failed during processing and cannot be resolved through this interface. 
            Technical investigation may be required.
          </Alert>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default JobDetailModal;
